/**
 * Portfolios → Images subpage.
 *
 * Mirrors Projects → Images visually and behaviorally: a single grid containing
 * every image associated with the portfolio. Portfolio-level PortfolioImage rows
 * appear first (in their original order), followed by per-location LocationImage
 * rows pulled per project location.
 *
 * Click an image → ImageReviewPopup (preview + note edit + delete), same as
 * Project Images.
 *
 * Note edits and deletes route to the appropriate slice based on `_kind`:
 *   portfolio → portfolioImagesSlice (no note update available)
 *   location  → locationImageSlice (full note + delete)
 *
 * Per-location images live in local state (raw axios fetch) so we don't pollute
 * the locationImages slice that other screens depend on.
 */

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import styled from 'styled-components'
import { Spinner as ShadcnSpinner } from 'components/ui/spinner'
import Spinner from 'components/Spinner'
import ImageGallery from 'components/ImageGallery/ImageGallery'
import ImageReviewPopup from 'components/ImageGallery/ImageReviewPopup'
import LocationImagesEmptyState from 'components/LocationImagesEmptyState'
import ImageUploadWizard, {
	commitImageUpload,
	portfolioImageAdapter,
} from 'components/ImageUploadWizard'
import { Button } from 'components/ui/button'
import { Upload } from 'lucide-react'
import {
	deletePortfolioImage,
	getPortfolioImages,
	updatePortfolioImage,
} from 'slices/portfolioImagesSlice'
import { deleteImage as deleteLocationImage, updateImage as updateLocationImage } from 'slices/locationImageSlice'

const Header = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
`

const HeaderTitle = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;

	h2 {
		font-size: 1.5rem;
		font-weight: 700;
		margin: 0;
	}

	span {
		font-size: 0.95rem;
		color: #4a5568;
	}
`

const HeaderActions = styled.div`
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
`

type LocationImageRow = {
	_id: string
	url: string
	type?: string
	note?: string
	createdAt?: string
	updatedAt?: string
	userid?: any
}

type DisplayImage = LocationImageRow & {
	_kind: 'portfolio' | 'location'
	/** Set on location images so delete + note-update can target the right local-state bucket. */
	_locationId?: string
}

const PortfolioImagesCN = () => {
	const { id } = useParams<{ id: string }>()
	const portfolioId = id!
	const dispatch = useDispatch()

	const userLogin = useSelector((state: any) => state.userLogin)
	const userInfo = userLogin?.userInfo

	const portfolio = useSelector((state: any) => state.portfolios?.entities?.[portfolioId])
	const portfolioLoading = useSelector((state: any) => state.portfolios?.isLoading)

	// ─── Portfolio-level images (Redux slice) ───────────────────────────────────
	const portfolioImageEntities = useSelector((s: any) => s.portfolioImages?.entities)
	const portfolioImagesLoading = useSelector((s: any) => s.portfolioImages?.isLoading)

	useEffect(() => {
		if (portfolioId) {
			dispatch((getPortfolioImages as any)(portfolioId))
		}
	}, [dispatch, portfolioId])

	const portfolioImages: DisplayImage[] = useMemo(() => {
		const ids: string[] = portfolio?.images || []
		return ids
			.map(imgId => portfolioImageEntities?.[imgId])
			.filter((img: any) => img && img._id)
			.map((img: any) => ({ ...img, _kind: 'portfolio' as const }))
	}, [portfolio?.images, portfolioImageEntities])

	// ─── Locations from the portfolio ───────────────────────────────────────────
	const locationsList = useMemo(() => {
		const raw = portfolio?.locations || []
		return raw
			.map((entry: any) => entry?.location)
			.filter(Boolean)
			.map((loc: any) => ({ _id: loc._id, assetName: loc.assetName || '' }))
	}, [portfolio?.locations])

	// ─── Per-location images (raw axios into local state) ───────────────────────
	const [locationImagesByLocId, setLocationImagesByLocId] = useState<Record<string, LocationImageRow[]>>({})
	const [locationImagesLoading, setLocationImagesLoading] = useState(false)

	useEffect(() => {
		if (!portfolio?._id || locationsList.length === 0) {
			setLocationImagesByLocId({})
			return
		}
		let cancelled = false
		setLocationImagesLoading(true)
		const token = userInfo?.token
		Promise.all(
			locationsList.map(async (loc: any) => {
				try {
					const { data } = await axios.get('/api/locationimages/images', {
						params: { locationid: loc._id },
						headers: token ? { Authorization: `Bearer ${token}` } : undefined,
					})
					return [loc._id, (data || []) as LocationImageRow[]] as const
				} catch {
					return [loc._id, [] as LocationImageRow[]] as const
				}
			}),
		).then(results => {
			if (cancelled) return
			setLocationImagesByLocId(Object.fromEntries(results))
			setLocationImagesLoading(false)
		})
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolio?._id, locationsList.length, userInfo?.token])

	// ─── Aggregated grid: portfolio first, then per-location in `locationsList` order ──
	const allImages: DisplayImage[] = useMemo(() => {
		const out: DisplayImage[] = [...portfolioImages]
		for (const loc of locationsList) {
			const imgs = locationImagesByLocId[loc._id] || []
			for (const img of imgs) {
				out.push({ ...img, _kind: 'location' as const, _locationId: loc._id })
			}
		}
		return out
	}, [portfolioImages, locationImagesByLocId, locationsList])

	const hasAnyImages = allImages.length > 0

	// ─── Preview / review popup ─────────────────────────────────────────────────
	const [reviewImage, setReviewImage] = useState<DisplayImage | null>(null)
	const [wizardOpen, setWizardOpen] = useState(false)

	const handlePreview = (img: any) => {
		setReviewImage(img as DisplayImage)
	}

	const handleDelete = async (img: any) => {
		const target = img as DisplayImage
		if (target._kind === 'portfolio') {
			await dispatch(deletePortfolioImage(target._id) as any)
		} else {
			await dispatch(deleteLocationImage({ imageid: target._id }) as any)
			// Drop from local state too so the grid updates without refetch
			if (target._locationId) {
				setLocationImagesByLocId(prev => {
					const next = { ...prev }
					next[target._locationId!] = (next[target._locationId!] || []).filter(
						i => i._id !== target._id,
					)
					return next
				})
			}
		}
		setReviewImage(null)
	}

	const handleUpdateNote = async (updated: any) => {
		const target = updated as DisplayImage
		if (target._kind === 'portfolio') {
			await dispatch(updatePortfolioImage(updated) as any)
			setReviewImage(null)
			return
		}
		await dispatch(updateLocationImage(updated) as any)
		// Mirror the update into local state
		if (target._locationId) {
			setLocationImagesByLocId(prev => {
				const next = { ...prev }
				next[target._locationId!] = (next[target._locationId!] || []).map(i =>
					i._id === target._id ? { ...i, note: updated.note } : i,
				)
				return next
			})
		}
		setReviewImage(null)
	}

	// ─── Upload (creates portfolio-level images, plus any notes) ────────────────
	// The wizard defers the Cloudinary upload until the user confirms, so the
	// note can be written alongside the images it describes.
	const uploadTarget = useMemo(
		() => ({
			entityLabel: portfolio?.portfolioName || 'this portfolio',
			folder: `mdt/portfolios/${portfolioId}`,
			onCommit: (commit: any, state: any) =>
				commitImageUpload(
					commit,
					portfolioImageAdapter({ dispatch, userId: userInfo._id, portfolioId }),
					{},
					state,
				),
		}),
		[dispatch, userInfo._id, portfolioId, portfolio?.portfolioName],
	)

	if (portfolioLoading || !portfolio) return <Spinner containerHeight="50vh" />

	return (
		<div>
			{hasAnyImages && (
				<Header>
					<HeaderTitle>
						<h2>Images</h2>
						<span>
							Maintain a visual record of this portfolio with organized photo uploads.
						</span>
					</HeaderTitle>
					<HeaderActions>
						<Button type="button" variant="primary" onClick={() => setWizardOpen(true)}>
							<span className="flex items-center gap-2">
								<Upload className="h-4 w-4" />
								Upload Images
							</span>
						</Button>
						{(portfolioImagesLoading || locationImagesLoading) && (
							<ShadcnSpinner containerHeight="auto" />
						)}
					</HeaderActions>
				</Header>
			)}

			{hasAnyImages ? (
				<ImageGallery
					locationImages={allImages as any}
					handleDeleteImage={handleDelete}
					handlePreviewImage={handlePreview}
				/>
			) : (
				<LocationImagesEmptyState onUploadClick={() => setWizardOpen(true)} />
			)}

			<ImageReviewPopup
				open={!!reviewImage}
				onClose={() => setReviewImage(null)}
				image={reviewImage as any}
				onUpdateNote={handleUpdateNote}
				onDeleteImage={handleDelete}
				disableDelete={reviewImage?._kind === 'location'}
				disableDeleteTitle="Location project images can only be removed from the location's own Images page."
			/>

			<ImageUploadWizard
				open={wizardOpen}
				onClose={() => setWizardOpen(false)}
				target={uploadTarget}
			/>
		</div>
	)
}

export default PortfolioImagesCN
